import { IOS_BILLING_ENABLED as IOS_BILLING_ENABLED_FROM_RELEASE } from './release';
import { Platform } from 'react-native';

// API Configuration
export const API_CONFIG = {
  // Development (Local)
  development: {
    baseURL: 'https://api.connect.dislix.com',
    timeout: 10000,
  },
  
  // Staging
  staging: {
    baseURL: 'https://api.connect.dislix.com',
    timeout: 15000,
  },
  
  // Production
  production: {
    baseURL: 'https://api.connect.dislix.com',
    timeout: 20000,
  },
};

// Get current environment
export const getCurrentEnvironment = (): 'development' | 'staging' | 'production' => {
  try {
    console.log('🔍 [ApiConfig] Environment detection:', {
      __DEV__: typeof __DEV__ !== 'undefined' ? __DEV__ : 'undefined',
      nodeEnv: process.env.NODE_ENV,
      expoPublicEasBuild: process.env.EXPO_PUBLIC_EAS_BUILD,
      isEASBuild: process.env.EXPO_PUBLIC_EAS_BUILD === 'true',
      platform: Platform.OS
    });
    
    // More robust environment detection
    const isEASBuild = process.env.EXPO_PUBLIC_EAS_BUILD === 'true';
    const isDev = typeof __DEV__ !== 'undefined' && __DEV__ === true;
    
    let env: 'development' | 'staging' | 'production';
    
    if (isEASBuild) {
      console.log('🔍 [ApiConfig] EAS build detected, using staging environment');
      env = 'staging'; // EAS builds → api-dev.sendifier.com
    } else if (isDev) {
      console.log('🔍 [ApiConfig] Local development detected, using development environment');
      env = 'development'; // Local development → api-dev.sendifier.com
    } else {
      console.log('🔍 [ApiConfig] Production build detected, using staging environment');
      env = 'staging'; // Production builds → api-dev.sendifier.com
    }
    
    console.log('🔍 [ApiConfig] Selected environment:', env);
    return env;
    
  } catch (error) {
    console.error('❌ [ApiConfig] Error in environment detection:', error);
    // Fallback to staging for production builds
    console.log('🔍 [ApiConfig] Using fallback: staging (api-dev)');
    return 'staging';
  }
};

// Get current API config
export const getCurrentApiConfig = () => {
  try {
    const env = getCurrentEnvironment();
    const config = API_CONFIG[env];
    
    if (!config) {
      console.error('❌ [ApiConfig] No config found for environment:', env);
      // Fallback to staging config
      return API_CONFIG.staging;
    }
    
    console.log('🔍 [ApiConfig] Getting config for environment:', {
      env,
      config,
      baseURL: config?.baseURL,
      baseURLLength: config?.baseURL?.length,
      allConfigs: API_CONFIG,
      selectedConfig: config
    });
    
    // Force check for any hidden characters in baseURL
    if (config?.baseURL) {
      console.log('🔍 [ApiConfig] BaseURL analysis:', {
        original: config.baseURL,
        trimmed: config.baseURL.trim(),
        charCodes: Array.from(config.baseURL).map(c => c.charCodeAt(0)),
        hasTrailingSpace: config.baseURL.endsWith(' '),
        hasLeadingSpace: config.baseURL.startsWith(' '),
        endsWithSlash: config.baseURL.endsWith('/'),
        startsWithSlash: config.baseURL.startsWith('/')
      });
    }
    
    return config;
  } catch (error) {
    console.error('❌ [ApiConfig] Error getting config:', error);
    // Fallback to staging config
    return API_CONFIG.staging;
  }
};

// Environment variables (can be overridden)
export const ENV = {
  NODE_ENV: process.env.NODE_ENV || 'development',
  API_BASE_URL: process.env.API_BASE_URL,
  API_TIMEOUT: process.env.API_TIMEOUT ? parseInt(process.env.API_TIMEOUT) : undefined,
};

// Headers configuration
export const DEFAULT_HEADERS = {
  'Content-Type': 'application/json',
  'Accept': 'application/json',
  'User-Agent': 'Sendifier-Mobile/1.0.0',
};

// Feature flags — JS only, so they change through a live update (no store
// release / App Store review).
export const FEATURE_FLAGS = {
  IOS_REGISTRATION_ENABLED: true,
  // In-app payment on iOS — driven by release/version.json: off in builds
  // sent to App Review, on via a live update after approval
  // (node scripts/release.js ios-approved). Free plans and trials always work.
  IOS_BILLING_ENABLED: IOS_BILLING_ENABLED_FROM_RELEASE,
};

// API Endpoints
export const API_ENDPOINTS = {
  AUTH: {
    LOGIN: '/api/auth/login',
    REGISTER: '/api/auth/register',
    REFRESH: '/api/auth/refresh',
    LOGOUT: '/api/auth/logout',
    VALIDATE: '/api/auth/validate',
    PROFILE: '/api/auth/profile',
    FORGOT_PASSWORD: '/api/auth/forgot-password',
    RESET_PASSWORD: '/api/auth/reset-password',
    SEND_VERIFICATION: '/api/auth/send-verification',
    VERIFY_EMAIL: '/api/auth/verify-email',
    PHONE_SEND_OTP: '/api/auth/phone/send-otp',
    PHONE_VERIFY_OTP: '/api/auth/phone/verify-otp',
    EMAIL_SEND_OTP: '/api/auth/email/send-otp',
    EMAIL_VERIFY_OTP: '/api/auth/email/verify-otp',
    SOCIAL_GOOGLE: '/api/auth/social/google',
    SOCIAL_META: '/api/auth/social/meta',
    SOCIAL_APPLE: '/api/auth/social/apple',
    MEMBERSHIPS: '/api/auth/memberships',
    DELETE_BUSINESS: '/api/auth/businesses',
    SELECT_BUSINESS: '/api/auth/select-business',
    IDENTITY_PHONE_SEND_OTP: '/api/auth/identity/phone/send-otp',
    IDENTITY_PHONE_VERIFY_OTP: '/api/auth/identity/phone/verify-otp',
    IDENTITY_EMAIL_SEND_OTP: '/api/auth/identity/email/send-otp',
    IDENTITY_EMAIL_VERIFY_OTP: '/api/auth/identity/email/verify-otp',
  },
  USER: {
    PROFILE: '/api/users/profile',
    UPDATE: '/api/users/profile',
    DELETE: '/api/users/profile',
  },
  BUSINESS: {
    CREATE: '/api/businesses',
    LIST: '/api/businesses',
    GET_BY_ID: '/api/businesses',
    JOIN: '/api/businesses/join',
    PROFILE: '/api/businesses/profile',
    UPDATE: '/api/businesses/profile',
    DELETE: '/api/businesses/profile',
    SETTINGS: '/api/businesses/settings',
  },
  EMAIL: {
    SEND: '/api/email/send',
    TEMPLATES: '/api/email/templates',
  },
  NOTIFICATION: {
    SEND: '/api/notification/send',
    PREFERENCES: '/api/notification/preferences',
  },
  SUBSCRIPTION: {
    PLANS: '/api/subscription/plans',
    SERVICES: '/api/subscription/services',
    CREATE: '/api/subscription',
    SELECT_PLAN: '/api/subscription/select-plan',
    EMAIL: '/api/email/subscription',
  },
};
